import React, { memo, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import withRetry from '../../../utils/withRetry';
import Search from './Search.web';
import { useDispatch, useSelector } from 'react-redux';
import { searchCloudAssets } from '../../../store/api/bites-api/calls/cloudAssets.calls';
import { ICloudAsset } from '../../../store/cloudAssets/cloudAssets.types';
import { activeOrganizationSelector } from '../../../store/auth/auth.selectors';
import {
  createCloudAssetCache,
  processCloudAsset,
  setCloudAsset,
  setCloudAssets,
} from '../../../store/cloudAssets/cloudAssets.slice';
import {
  addToTimeline,
  addUndo,
  deleteProcessingTimelineItemState,
  removeEmptyTimelineLayers,
  removeTimelineItem,
  removeUndoRedoById,
  saveVideo,
  setProcessingTimelineItemState,
  setReplacingSelectedTimelineItem,
  updateCanvas,
  updateTimelineItem,
  updateTimelineItemLayer,
  updateTimelineItemWithCloudAssetDuration,
} from '../../../store/videoEditor/videoEditor.slice';
import { ITimelineItem } from '../../../store/videoEditor/videoEditor.types';
import { v4 as uuid } from 'uuid';
import { getItemCanvasSize } from '../utils/getItemCanvasSize';
import {
  replacingSelectedTimelineItemSelector,
  selectedTimelineItemIdSelector,
  selectedTimelineItemLayerSelector,
  timelineItemSeletor,
  videoResolutionSeletor,
} from '../../../store/videoEditor/videoEditor.selectors';
import { retryFunctions } from '../../../store/videoEditor/videoEditor.data';
import { getIsItemOnTimeline } from '../utils/getIsItemOnTimeline';
import { cloudFileTypeSelector } from '../../../store/videoEditorLeftSidebar/videoEditorLeftSidebar.selectors';
import { logError } from '../../../store/appActivity/appActivity.slice';
import CloudSearchItem from './CloudSearchItem';
import { IInitialState } from '../../../store/videoEditorLeftSidebar/videoEditorLeftSidebar.types';
import { useIsMounted } from '../../../hooks/useIsMounted';
import { PreviewVideo } from './preview/PreviewVideo';
import { PreviewImage } from './preview/PreviewImage';
import { PreviewAudio } from './preview/PreviewAudio';

type Props = {
  cloudResults: IInitialState['cloudResults'];
  renderFilterControls?: () => React.ReactElement;
  additionalRequestFilters?: {
    favoriteByUserId?: number;
    isBrandAsset?: boolean;
    usedInVideoId?: string;
    generationId?: string;
  };
  setCloudResults: (cloudResults: Partial<IInitialState['cloudResults']>) => void;
  setIsPreviewPanelVisible: (isVisible: boolean) => void;
  isFlaggedToReset?: boolean;
  previewPanelRef: RefObject<HTMLDivElement>;
};
const Cloud = ({
  cloudResults,
  renderFilterControls,
  additionalRequestFilters,
  setCloudResults,
  setIsPreviewPanelVisible,
  isFlaggedToReset,
  previewPanelRef,
}: Props) => {
  const dispatch = useDispatch();
  const isMountedRef = useIsMounted();

  const org = useSelector(activeOrganizationSelector);
  const resolution = useSelector(videoResolutionSeletor);
  const fileType = useSelector(cloudFileTypeSelector);
  const replacingSelectedTimelineItem = useSelector(replacingSelectedTimelineItemSelector);

  const requestIdRef = useRef(0);

  const handleSearch = useCallback(
    async ({
      query,
      withReset,
      withDelayedReset,
    }: {
      query: string;
      withReset?: boolean;
      withDelayedReset?: boolean;
    }) => {
      const requestId = ++requestIdRef.current;

      try {
        dispatch(
          setCloudResults({
            data: withReset ? null : cloudResults.data,
            page: withReset ? 0 : cloudResults.page,
            totalPages: withReset ? null : cloudResults.totalPages,
            isLoading: true,
            error: null,
          }),
        );

        const { data: result } = await withRetry(
          () =>
            searchCloudAssets({
              filters: {
                orgId: org.id,
                search: query?.trim() || undefined,
                fileType: fileType || undefined,
                ...(additionalRequestFilters || {}),
              },
              page: withReset || withDelayedReset ? 0 : cloudResults.page + 1,
            }),
          {
            errorContext: {
              data: {
                action: 'Cloud.handleSearch',
              },
            },
          },
        );

        if (requestId !== requestIdRef.current || !isMountedRef.current) {
          return;
        }

        const cloudAssets = result.results.map(({ cloudAsset }) => cloudAsset);
        dispatch(setCloudAssets({ cloudAssets }));

        const data = result.results.map(({ cloudAsset }) => cloudAsset.id);
        dispatch(
          setCloudResults({
            data: withReset || withDelayedReset ? data : [...(cloudResults.data || []), ...data],
            page: result.page,
            totalPages: result.totalPages,
            isLoading: false,
          }),
        );
      } catch (error) {
        if (requestId !== requestIdRef.current || !isMountedRef.current) {
          return;
        }

        dispatch(
          logError({
            event: 'Cloud.handleSearch: error',
            data: {
              error,
            },
          }),
        );

        dispatch(
          setCloudResults({
            data: withDelayedReset ? [] : cloudResults.data,
            page: withDelayedReset ? 0 : cloudResults.page,
            totalPages: withDelayedReset ? null : cloudResults.totalPages,
            isLoading: false,
            error,
          }),
        );
      }
    },
    [
      additionalRequestFilters,
      cloudResults.data,
      cloudResults.page,
      cloudResults.totalPages,
      dispatch,
      fileType,
      isMountedRef,
      org.id,
      setCloudResults,
    ],
  );

  const getItemKey = useCallback((id: ICloudAsset['id']) => id, []);

  const [selectedItem, setSelectedItem] = useState<ICloudAsset>(null);

  const selectedTimelineItemId = useSelector(selectedTimelineItemIdSelector);
  const selectedTimelineItem = useSelector(timelineItemSeletor(selectedTimelineItemId));
  const selectedTimelineItemLayer = useSelector(selectedTimelineItemLayerSelector);

  const handleUseItem = useCallback(
    ({ withReplace }: { withReplace?: boolean } = {}) => {
      const cloudAsset: ICloudAsset = selectedItem;

      dispatch(setCloudAsset({ cloudAsset }));

      setIsPreviewPanelVisible(false);
      setSelectedItem(null);
      dispatch(setReplacingSelectedTimelineItem(false));

      const timelineItemId = uuid();
      const timelineItem: Omit<ITimelineItem, 'start' | 'end'> = {
        id: timelineItemId,
        type: cloudAsset.fileType as ITimelineItem['type'],
        cloudAssetId: cloudAsset.id,
        x: 0,
        y: 0,
      };

      let start: number;
      let layerId: string;
      if (withReplace && selectedTimelineItem && selectedTimelineItemLayer) {
        if (timelineItem.type === selectedTimelineItem.type) {
          start = selectedTimelineItem.start;
          layerId = selectedTimelineItemLayer.id;
        }

        dispatch(
          removeTimelineItem({
            timelineItemId: selectedTimelineItemId,
            fromTimeline: true,
          }),
        );
      }

      dispatch(
        addToTimeline({
          timelineItem,
          duration:
            cloudAsset.fileType === 'video' || cloudAsset.fileType === 'audio' ? cloudAsset.fileMeta.duration : null,
          start,
          layerId,
        }),
      );
      dispatch(
        updateTimelineItemLayer({
          timelineItemId,
        }),
      );
      dispatch(removeEmptyTimelineLayers());

      const undoId = uuid();
      dispatch(
        addUndo({
          id: undoId,
        }),
      );

      const retryFunctionId = uuid();
      const processingLabel = `Cloud ${cloudAsset.fileType}`;

      const applyItemFunction = async () => {
        dispatch(
          setProcessingTimelineItemState({
            timelineItemId,
            label: processingLabel,
            status: 'PROCESSING',
            retryFunctionId,
          }),
        );

        dispatch(
          createCloudAssetCache({
            cloudAsset,
            timelineItems: [timelineItem],
            onCacheReady: () => {
              dispatch(
                removeUndoRedoById({
                  id: undoId,
                }),
              );

              dispatch(
                setProcessingTimelineItemState({
                  timelineItemId,
                  label: processingLabel,
                  status: 'DONE',
                }),
              );
              setTimeout(() => {
                dispatch(
                  deleteProcessingTimelineItemState({
                    timelineItemId,
                  }),
                );
              }, 5000);

              delete retryFunctions[retryFunctionId];

              const isOnTimeline = getIsItemOnTimeline(timelineItemId);

              if (!isOnTimeline) {
                return;
              }

              if (cloudAsset.fileType !== 'audio') {
                const canvasSize = getItemCanvasSize({
                  width: cloudAsset.fileMeta.width,
                  height: cloudAsset.fileMeta.height,
                  resolution,
                  scaleUp: cloudAsset.fileType !== 'gif',
                });

                timelineItem.width = canvasSize.width;
                timelineItem.height = canvasSize.height;
              }

              dispatch(updateTimelineItem(timelineItem));

              if (cloudAsset.fileType === 'video' || cloudAsset.fileType === 'audio') {
                dispatch(
                  updateTimelineItemWithCloudAssetDuration({
                    timelineItemId,
                  }),
                );
              }

              dispatch(updateCanvas({}));
              dispatch(
                addUndo({
                  id: undoId,
                }),
              );
              dispatch(saveVideo({}));
            },
            onFail: (error: any) => {
              dispatch(
                setProcessingTimelineItemState({
                  timelineItemId,
                  label: processingLabel,
                  status: 'ERROR',
                  error: {
                    details: error,
                  },
                  retryFunctionId,
                }),
              );
            },
          }),
        );

        dispatch(processCloudAsset({ cloudAsset }));
      };

      retryFunctions[retryFunctionId] = applyItemFunction;
      applyItemFunction();
    },
    [
      dispatch,
      resolution,
      selectedItem,
      selectedTimelineItem,
      selectedTimelineItemId,
      selectedTimelineItemLayer,
      setIsPreviewPanelVisible,
    ],
  );

  const handleReplaceItem = useCallback(() => {
    handleUseItem({ withReplace: true });
  }, [handleUseItem]);

  const handleItemSelect = useCallback(
    (item) => {
      setIsPreviewPanelVisible(true);
      setSelectedItem(item);
    },
    [setIsPreviewPanelVisible],
  );

  const handleItemUnselect = useCallback(() => {
    setIsPreviewPanelVisible(false);
    setSelectedItem(null);
  }, [setIsPreviewPanelVisible]);

  const renderItem = useCallback(
    ({ item }: { item: ICloudAsset['id'] }) => {
      return <CloudSearchItem cloudAssetId={item} onSelect={handleItemSelect} />;
    },
    [handleItemSelect],
  );

  useEffect(() => {
    return () => {
      setIsPreviewPanelVisible(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* <S.SearchContainer> */}
      <Search
        results={cloudResults.data}
        isLoading={cloudResults.isLoading}
        isError={cloudResults.error}
        hasMore={cloudResults.totalPages && cloudResults.totalPages > cloudResults.page + 1}
        withSearchInput
        layout='list'
        onSearch={handleSearch}
        renderFilterControls={renderFilterControls}
        renderItem={renderItem}
        getItemKey={getItemKey}
        isFlaggedToReset={isFlaggedToReset}
        key={fileType}
      />
      {/* </S.SearchContainer> */}
      {selectedItem?.fileType === 'video' &&
        createPortal(
          <PreviewVideo
            url={selectedItem.storage.url}
            onAdd={handleUseItem}
            onReplace={replacingSelectedTimelineItem ? handleReplaceItem : undefined}
            onClose={handleItemUnselect}
          />,
          previewPanelRef.current,
        )}
      {(selectedItem?.fileType === 'image' || selectedItem?.fileType === 'gif') &&
        createPortal(
          <PreviewImage
            url={selectedItem.storage.url}
            onAdd={handleUseItem}
            onReplace={replacingSelectedTimelineItem ? handleReplaceItem : undefined}
            onClose={handleItemUnselect}
          />,
          previewPanelRef.current,
        )}
      {selectedItem?.fileType === 'audio' &&
        createPortal(
          <PreviewAudio
            url={selectedItem.storage.url}
            onAdd={handleUseItem}
            onReplace={replacingSelectedTimelineItem ? handleReplaceItem : undefined}
            onClose={handleItemUnselect}
          />,
          previewPanelRef.current,
        )}
    </>
  );
};
export default memo(Cloud);
